import { useEffect } from 'react'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  HumanizedSelectField,
  NumberField,
  TextField,
} from '@/components/ui'
import { useToast } from '@/hooks'
import {
  ACCOUNT_TYPES,
  CATEGORY_OPTIONS,
  getCategoryDetailOptions,
  PAYMENT_CHANNELS_OPTIONS,
  type Transaction,
  type UpdateChecksDTO,
  type UpdateTransactionDTO,
  useProjectMutating,
  useProjectMutations,
} from '@/modules/projects'
import type { TransactionsTableVariant } from '@/modules/projects/components/tables/transactions/columns'
import {
  useDialogActions,
  useDialogData,
  useDialogIsOpen,
  useForm,
} from '@/stores'
import { humanizeText, snakelize } from '@/utils'

const dialogId = 'bulk-edit-transactions'
type Fields =
  | 'year'
  | 'accountNumber'
  | 'shareNumber'
  | 'accountType'
  | 'merchant'
  | 'paymentChannel'
  | 'category'
  | 'institution'
  | 'accountOwner'
  | 'checkNumber'

export const BulkEditTransactionsDialog = () => {
  const { successToast, errorToast } = useToast()
  const open = useDialogIsOpen(dialogId)
  const { closeDialog } = useDialogActions()
  const { data, errors, setFieldValue, setFieldError } = useForm()
  const { updateTransactions, updateChecks } = useProjectMutations()
  const isUpdating = useProjectMutating()
  const { field, transactions, variant } = useDialogData({
    transactions: [],
    variant: 'transactions',
  }) as {
    field?: Fields
    transactions: Transaction[]
    variant?: TransactionsTableVariant
  }
  const isChecks = variant === 'checks'
  const total = transactions?.length || 0
  const transaction = total === 1 ? transactions[0] : null
  const noun = isChecks
    ? transaction
      ? 'Check'
      : 'Checks'
    : transaction
      ? 'Transaction'
      : 'Transactions'
  const maxYear = new Date().getFullYear() + 1

  useEffect(() => {
    if (!total && !field) return
    if (!open) return

    const value =
      (transactions[0][field as keyof Transaction] as string) || undefined
    if (field === 'category') {
      const categoryDetailValue = transactions[0].categoryDetail
      setCategory(value, categoryDetailValue)

      return
    }

    setFieldValue(field as string, value)
  }, [open, transactions])

  const categoryDetailOptions = getCategoryDetailOptions(
    data.category as string,
  )

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!field) return

    if (field === 'year') {
      const year = Number(data.year)
      if (year < 1900 || year > maxYear) {
        setFieldError('year', `Year must be between 1900 and ${maxYear}`)
        return
      }
    }

    const body = {
      [snakelize(field)]: data[field],
    } as Partial<UpdateTransactionDTO> & Partial<UpdateChecksDTO>
    if (field === 'category') {
      body.category_detail = data.categoryDetail as string
    }
    const copy = transaction
      ? noun.toLowerCase()
      : `${total} ${noun.toLowerCase()}`

    const promise = isChecks
      ? updateChecks(transactions, body as Partial<UpdateChecksDTO>)
      : updateTransactions(transactions, body as Partial<UpdateTransactionDTO>)

    promise
      .then(() => closeDialog(true))
      .then(() => successToast(`Updated ${copy} successfully`))
      .catch((error) => errorToast(`Failed to update ${copy}`, error))
  }

  const setCategory = (value?: string, detailValue?: string) => {
    const newOptions = getCategoryDetailOptions(value)
    setFieldValue('category', value)
    setFieldValue('categoryDetail', humanizeText(detailValue || newOptions[0]))
  }

  return (
    <Dialog open={open} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Edit {noun}</DialogTitle>
          <DialogDescription>
            This will update{' '}
            {transaction
              ? `this ${noun.toLowerCase()}`
              : `${total} ${noun.toLowerCase()}`}
            . This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <div className="py-4 space-y-4">
            <div className="space-y-6">
              {field === 'year' && (
                <NumberField
                  label="Year"
                  value={data?.year as string}
                  placeholder={`e.g. ${new Date().getFullYear()}`}
                  error={errors?.year}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFieldValue('year', e.target.value)
                  }
                  min={1900}
                  max={maxYear}
                  className="w-full"
                  required
                />
              )}

              {field === 'accountNumber' && (
                <NumberField
                  label="Account"
                  value={data?.accountNumber as string}
                  placeholder="Type the account number"
                  error={errors?.accountNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFieldValue('accountNumber', e.target.value)
                  }
                  maxLength={4}
                  className="w-full"
                  helpText="Exact 4 last numbers"
                  required
                />
              )}

              {field === 'checkNumber' && (
                <NumberField
                  label="Check Number"
                  value={data?.checkNumber as string}
                  placeholder="Type the check number"
                  error={errors?.checkNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFieldValue('checkNumber', e.target.value)
                  }
                  className="w-full"
                  required
                />
              )}

              {field === 'accountType' && !isChecks && (
                <HumanizedSelectField
                  label="Account Type"
                  placeholder="Select an account type"
                  value={data?.accountType as string}
                  error={errors?.accountType}
                  className="w-full"
                  onValueChange={(value: string) => {
                    setFieldValue('accountType', value)
                  }}
                  options={ACCOUNT_TYPES as unknown as string[]}
                  required
                />
              )}

              {field === 'shareNumber' && !isChecks && (
                <NumberField
                  label="Share Number"
                  value={data?.shareNumber as string}
                  placeholder="Type the share number"
                  error={errors?.shareNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFieldValue('shareNumber', e.target.value)
                  }
                  className="w-full"
                  maxLength={3}
                  padLength={2}
                  helpText="2-3 digits"
                />
              )}

              {field === 'merchant' && !isChecks && (
                <TextField
                  label="Merchant"
                  value={data?.merchant as string}
                  className="w-full"
                  placeholder="Type the merchant name"
                  error={errors?.merchant}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFieldValue('merchant', e.target.value)
                  }
                  required
                />
              )}

              {field === 'paymentChannel' && !isChecks && (
                <HumanizedSelectField
                  label="Payment Channel"
                  placeholder="Select a payment channel"
                  value={data?.paymentChannel as string}
                  className="w-full"
                  error={errors?.paymentChannel}
                  onValueChange={(value: string) => {
                    setFieldValue('paymentChannel', value)
                  }}
                  options={PAYMENT_CHANNELS_OPTIONS}
                  required
                />
              )}

              {field === 'category' && !isChecks && (
                <div className="grid grid-cols-2 gap-4 items-start">
                  <HumanizedSelectField
                    label="Category"
                    placeholder="Select a category"
                    value={data?.category as string}
                    error={errors?.category}
                    onValueChange={setCategory}
                    className="w-full"
                    options={CATEGORY_OPTIONS}
                    required
                  />
                  <HumanizedSelectField
                    key={data?.category as string}
                    label="Category Detail"
                    placeholder="Select a category detail"
                    value={data?.categoryDetail as string}
                    error={errors?.categoryDetail}
                    className="w-full"
                    options={categoryDetailOptions as unknown as string[]}
                    onValueChange={(value: string) => {
                      setFieldValue('categoryDetail', value)
                    }}
                    helpText={
                      data?.category ? undefined : 'Select category first'
                    }
                    disabled={!data?.category}
                    required
                  />
                </div>
              )}

              {field === 'institution' && (
                <TextField
                  label="Institution"
                  value={data?.institution as string}
                  className="w-full"
                  placeholder="Type the institution name"
                  error={errors?.institution}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFieldValue('institution', e.target.value)
                  }
                  required
                />
              )}

              {field === 'accountOwner' && (
                <TextField
                  label="Account Owner"
                  value={data?.accountOwner as string}
                  className="w-full"
                  placeholder="Type the account owner name"
                  error={errors?.accountOwner}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFieldValue('accountOwner', e.target.value)
                  }
                  required
                />
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button theme="outline" onClick={() => closeDialog(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isUpdating}>
              Edit {noun}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

BulkEditTransactionsDialog.id = dialogId
